/*
 * kemi-scroll-frames.js — scroll-scrubbing tipo Apple sin GSAP/Lenis.
 * Reproduce una secuencia de imágenes (frames webp) sobre un <canvas> según el
 * progreso de scroll de un "riel" alto que contiene un canvas `sticky`.
 *
 * El elemento espera dentro:
 *   <script type="application/json" class="kemi-frames__data">["url1", ...]</script>
 *   <div class="kemi-frames__sticky"><canvas class="kemi-frames__canvas"></canvas></div>
 *
 * - Progreso 0→1 mapeado al índice de frame, dibujado con "cover" + devicePixelRatio.
 * - Precarga y pre-decodifica los frames; si el exacto no cargó, usa el más cercano.
 * - Se refresca en rAF sólo cuando hay scroll/resize (sin bucle continuo).
 * - Con prefers-reduced-motion muestra el primer frame estático.
 */

class KemiScrollFrames extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('.kemi-frames__data');
    const canvas = this.querySelector('.kemi-frames__canvas');
    if (!dataEl || !canvas) return;

    let urls = [];
    try { urls = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!urls.length) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const count = urls.length;
    const images = new Array(count);
    const state = { frame: 0 };
    let ticking = false;

    const ready = (img) => !!img && img.complete && img.naturalWidth > 0;

    const drawCover = (img) => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cw = canvas.clientWidth;
      const ch = canvas.clientHeight;
      if (canvas.width !== Math.round(cw * dpr) || canvas.height !== Math.round(ch * dpr)) {
        canvas.width = Math.round(cw * dpr);
        canvas.height = Math.round(ch * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cw, ch);
      const ir = img.width / img.height;
      const cr = cw / ch;
      let dw, dh, dx, dy;
      if (cr > ir) { dw = cw; dh = cw / ir; dx = 0; dy = (ch - dh) / 2; }
      else { dh = ch; dw = ch * ir; dx = (cw - dw) / 2; dy = 0; }
      ctx.drawImage(img, dx, dy, dw, dh);
    };

    const render = () => {
      const target = Math.round(state.frame);
      let img = images[target];
      if (!ready(img)) {
        for (let d = 1; d < count; d++) {
          if (ready(images[target - d])) { img = images[target - d]; break; }
          if (ready(images[target + d])) { img = images[target + d]; break; }
        }
      }
      if (ready(img)) drawCover(img);
    };

    // Precarga + pre-decodificación en segundo plano.
    let firstDrawn = false;
    for (let i = 0; i < count; i++) {
      const img = new Image();
      img.decoding = 'async';
      img.src = urls[i];
      img.onload = () => {
        if (img.decode) img.decode().catch(() => {});
        if (!firstDrawn && i === 0) { firstDrawn = true; render(); }
      };
      images[i] = img;
    }

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /** Progreso de scroll del riel `sticky`: 0 al entrar, 1 al salir. */
    const progress = () => {
      const rect = this.getBoundingClientRect();
      const scrollable = this.offsetHeight - window.innerHeight;
      if (scrollable <= 0) return 0;
      return Math.min(Math.max(-rect.top / scrollable, 0), 1);
    };

    // Primer pintado en cuanto haya algo disponible.
    if (ready(images[0])) { render(); firstDrawn = true; }

    // Bucle rAF que sondea el progreso cada frame y redibuja SOLO cuando cambia el
    // índice de frame. No depende de eventos `scroll` (Horizon scrollea un contenedor
    // interno .page-wrapper en desktop, cuyos eventos no llegan a window de forma
    // fiable), así que el scrub funciona con cualquier scroller. Coste mínimo: un
    // getBoundingClientRect por frame y un drawImage solo al cambiar de frame.
    let lastFrame = -1;
    const loop = () => {
      this._raf = requestAnimationFrame(loop);
      const target = reduced ? 0 : Math.round(progress() * (count - 1));
      if (target !== lastFrame) {
        lastFrame = target;
        state.frame = target;
        render();
      }
    };
    loop();

    // En resize forzamos un redibujo (el buffer del canvas cambió de tamaño).
    const onResize = () => { lastFrame = -1; };
    window.addEventListener('resize', onResize, { passive: true });

    this._cleanup = () => {
      cancelAnimationFrame(this._raf);
      window.removeEventListener('resize', onResize);
    };
  }

  disconnectedCallback() {
    if (this._cleanup) this._cleanup();
  }
}

if (!customElements.get('kemi-scroll-frames')) {
  customElements.define('kemi-scroll-frames', KemiScrollFrames);
}
